import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  BarChart3,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Circle,
  Heart,
  Image as ImageIcon,
  Info,
  MessageCircle,
  Pencil,
  RefreshCw,
  Search,
  Send,
  Sparkles,
  Tag,
  Users,
  X,
  Loader2,
} from "lucide-react";
import {
  useInstagramComments,
  useInstagramConnection,
  useInstagramConversationWindow,
  useInstagramConversations,
  useInstagramDashboard,
  useInstagramDrafts,
  useInstagramMedia,
} from "../instagram/hooks/useInstagram";
import { instagramApi } from "../../api/instagram";
import { uploadMedia } from "../media/api/mediaApi";
import { useUiLanguage } from "../../i18n/UiLanguageProvider";
import { InstagramV2ComposerModal } from "./InstagramV2ComposerModal";
import {
  formatDateHeading,
  formatFullDateTime,
  formatNumber,
  formatRelativeTime,
  formatTimeOnly,
  getInitial,
} from "../instagram/utils/format";
import type {
  CommentItem as ApiCommentItem,
  ConversationAttachment,
  ContentDraft,
  ConversationListItem,
  ConversationMessage,
  MediaItem,
  RecentMediaSummaryItem,
  WorkflowStatus,
} from "../instagram/types";
import "./instagramV2.css";

type TabKey = "dashboard" | "inbox" | "comments" | "content";
type Tone = "violet" | "pink" | "orange" | "mint";
type InboxFilterKey = "all" | "pending" | "important";
type PreviewFrameKind = "tall" | "portrait" | "square" | "landscape";

type UiConversation = {
  id: string;
  name: string;
  handle: string;
  avatar: string | null;
  preview: string;
  time: string;
  unread: number;
  updatedAt: string;
  isRead: boolean;
  status: WorkflowStatus;
};

type UiComment = {
  id: string;
  mediaId: string;
  author: string;
  avatar: string | null;
  mediaThumb: string | null;
  mediaTitle: string;
  comment: string;
  time: string;
  status: WorkflowStatus | "replied";
  replies: number;
  isReply?: boolean;
  parentCommentId?: string | null;
  parentAuthor?: string | null;
};

type UiCommentThread = {
  id: string;
  mediaId: string;
  author: string;
  avatar: string | null;
  mediaThumb: string | null;
  mediaTitle: string;
  comment: string;
  time: string;
  status: WorkflowStatus | "replied";
  replies: Array<{
    id: string;
    author: string;
    comment: string;
    time: string;
    parentAuthor: string | null;
  }>;
};

type CommentReplyTarget = {
  parentCommentId: string;
  targetUserName: string;
};

type UiPostCommentDetail = {
  id: string;
  author: string;
  comment: string;
  time: string;
  status: WorkflowStatus | "replied";
  isReply: boolean;
  parentAuthor: string | null;
};

type UiPost = {
  id: string;
  title: string;
  thumb: string | null;
  date: string;
  status: "published" | "scheduled" | "draft" | "failed" | "publishing";
  likes: number;
  comments: number;
  caption: string;
  images: string[];
  mediaType: MediaItem["media_type"] | ContentDraft["content_type"];
  videoUrl: string | null;
  source: "media" | "draft";
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const parseLeadingReplyMention = (text: string, fallbackAuthor?: string | null) => {
  const trimmed = text.trimStart();
  const explicitMatch = trimmed.match(/^@([A-Za-z0-9._]+)\s+(.*)$/s);
  if (explicitMatch) {
    return {
      mention: explicitMatch[1],
      body: explicitMatch[2],
    };
  }

  if (fallbackAuthor) {
    const pattern = new RegExp(`^@${escapeRegExp(fallbackAuthor)}\\s+`, "i");
    if (pattern.test(trimmed)) {
      return {
        mention: fallbackAuthor,
        body: trimmed.replace(pattern, ""),
      };
    }
  }

  return {
    mention: fallbackAuthor ?? null,
    body: text,
  };
};

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1200&q=80";


function resolveCommentAuthor(
  rawAuthor: string | null | undefined,
  pick: (ja: string, en: string) => string,
) {
  const trimmed = (rawAuthor || "").trim();
  return trimmed || pick("Instagramユーザー", "Instagram user");
}

function mapConversation(
  item: ConversationListItem,
  pick: (ja: string, en: string) => string,
): UiConversation {
  return {
    id: item.id,
    name: item.display_name || item.user_name,
    handle: `@${item.user_name}`,
    avatar: item.avatar_url,
    preview: item.preview || pick("メッセージはまだありません。", "No messages yet."),
    time: formatRelativeTime(item.updated_at),
    unread: item.unread_count,
    updatedAt: item.updated_at,
    isRead: item.is_read,
    status: item.status,
  };
}

function messagePreview(text: string, attachments: ConversationAttachment[], pick: (ja: string, en: string) => string) {
  const trimmed = text.trim();
  if (trimmed) return trimmed;
  if (attachments.length > 0) {
    const firstType = attachments[0]?.type || "file";
    if (firstType === "image") return pick("[画像]", "[Image]");
    if (firstType === "video") return pick("[動画]", "[Video]");
    return pick("[添付ファイル]", "[Attachment]");
  }
  return pick("表示できないメッセージがあります", "A message can't be displayed");
}

function mapComment(
  item: ApiCommentItem,
  pick: (ja: string, en: string) => string,
): UiComment {
  return {
    id: item.id,
    mediaId: item.media_id,
    author: resolveCommentAuthor(item.user_name, pick),
    avatar: null,
    mediaThumb: item.media_thumbnail_url,
    mediaTitle: item.media_caption?.trim() || pick("Instagram 投稿", "Instagram post"),
    comment: item.text,
    time: formatRelativeTime(item.created_at),
    status: item.replies.length > 0 ? "replied" : item.status,
    replies: item.replies.length,
  };
}

function flattenComments(
  items: ApiCommentItem[],
  pick: (ja: string, en: string) => string,
): UiComment[] {
  return items.flatMap((item) => {
    const topLevel = mapComment(item, pick);
    const replies = item.replies.map<UiComment>((reply) => ({
      id: reply.id,
      mediaId: item.media_id,
      author: resolveCommentAuthor(reply.user_name, pick),
      avatar: null,
      mediaThumb: item.media_thumbnail_url,
      mediaTitle: item.media_caption?.trim() || "Instagram 投稿",
      comment: reply.text,
      time: formatRelativeTime(reply.created_at),
      status: "replied",
      replies: 0,
      isReply: true,
      parentCommentId: item.id,
      parentAuthor: resolveCommentAuthor(item.user_name, pick),
    }));
    return [topLevel, ...replies];
  });
}

function mapCommentThread(item: ApiCommentItem): UiCommentThread {
  return {
    id: item.id,
    mediaId: item.media_id,
    author: resolveCommentAuthor(item.user_name, (ja) => ja),
    avatar: null,
    mediaThumb: item.media_thumbnail_url,
    mediaTitle: item.media_caption?.trim() || "Instagram 投稿",
    comment: item.text,
    time: formatRelativeTime(item.created_at),
    status: item.replies.length > 0 ? "replied" : item.status,
    replies: item.replies.map((reply) => ({
      id: reply.id,
      author: resolveCommentAuthor(reply.user_name, (ja) => ja),
      comment: reply.text,
      time: formatRelativeTime(reply.created_at),
      parentAuthor: resolveCommentAuthor(item.user_name, (ja) => ja),
    })),
  };
}

function mapMediaPost(item: MediaItem): UiPost {
  const childImages = (item.children ?? [])
    .map((child) => (
      child.media_type === "VIDEO" || child.media_type === "REEL"
        ? child.thumbnail_url || child.media_url
        : child.media_url || child.thumbnail_url
    ))
    .filter((value): value is string => Boolean(value));
  const isVideoPost = item.media_type === "VIDEO" || item.media_type === "REEL";
  const images = childImages.length > 0
    ? childImages
    : [isVideoPost ? (item.thumbnail_url || item.media_url || FALLBACK_IMAGE) : (item.media_url || item.thumbnail_url || FALLBACK_IMAGE)]
      .filter(Boolean) as string[];
  const thumb = item.thumbnail_url || images[0] || item.media_url || FALLBACK_IMAGE;
  return {
    id: item.id,
    title: item.caption?.trim() ? item.caption.slice(0, 40) : item.media_type,
    thumb,
    date: formatFullDateTime(item.timestamp),
    status: "published",
    likes: item.like_count,
    comments: item.comments_count,
    caption: item.caption || "キャプション未設定",
    images,
    mediaType: item.media_type,
    videoUrl: isVideoPost ? item.media_url : null,
    source: "media",
  };
}

function mapDraftPost(item: ContentDraft): UiPost {
  const images = item.media_items
    .map((media) => media.url)
    .filter((value): value is string => Boolean(value));
  const thumb = images[0] || FALLBACK_IMAGE;
  return {
    id: item.id,
    title: item.caption?.trim() ? item.caption.slice(0, 40) : "下書き投稿",
    thumb,
    date: formatFullDateTime(item.scheduled_at || item.updated_at),
    status: item.status,
    likes: 0,
    comments: 0,
    caption: item.caption || "キャプション未設定",
    images: images.length > 0 ? images : [FALLBACK_IMAGE],
    mediaType: item.content_type,
    videoUrl: null,
    source: "draft",
  };
}

function mediaSummaryTitle(
  item: RecentMediaSummaryItem | null,
  pick: (ja: string, en: string) => string,
): string {
  if (!item) return pick("まだ投稿がありません", "No posts yet");
  const title = (item.title || item.caption || "").trim();
  if (title) return title.slice(0, 40);
  const type = (item.media_type || "").toString().trim();
  if (type) return type;
  return item.timestamp
    ? pick(`投稿 ${formatFullDateTime(item.timestamp)}`, `Post ${formatFullDateTime(item.timestamp)}`)
    : pick("キャプションなしの投稿", "Post without caption");
}

function mapSummaryMediaPost(
  item: RecentMediaSummaryItem,
  pick: (ja: string, en: string) => string,
): UiPost {
  const title = mediaSummaryTitle(item, pick);
  const thumb = item.thumbnail_url || FALLBACK_IMAGE;
  return {
    id: item.id,
    title,
    thumb,
    date: item.timestamp ? formatFullDateTime(item.timestamp) : pick("日時未取得", "Date unavailable"),
    status: "published",
    likes: item.like_count,
    comments: item.comments_count,
    caption: item.caption || pick("キャプション未設定", "No caption yet"),
    images: [thumb],
    mediaType: (item.media_type || "IMAGE") as UiPost["mediaType"],
    videoUrl: null,
    source: "media",
  };
}

function splitMessagesByDay(messages: ConversationMessage[]) {
  const sorted = sortMessagesByCreatedAt(messages);
  const groups: Array<{ heading: string; items: ConversationMessage[] }> = [];
  for (const message of sorted) {
    const heading = formatDateHeading(message.created_at);
    const last = groups[groups.length - 1];
    if (!last || last.heading !== heading) {
      groups.push({ heading, items: [message] });
    } else {
      last.items.push(message);
    }
  }
  return groups;
}

function sortMessagesByCreatedAt(messages: ConversationMessage[]) {
  return [...messages].sort((a, b) => {
    const left = Date.parse(a.created_at || "");
    const right = Date.parse(b.created_at || "");
    if (Number.isNaN(left) && Number.isNaN(right)) return 0;
    if (Number.isNaN(left)) return 1;
    if (Number.isNaN(right)) return -1;
    return left - right;
  });
}

function renderInitialAvatar(name: string, size: "default" | "small" = "default") {
  const className = size === "small"
    ? "igv2-avatar igv2-avatar--brand igv2-avatar--small"
    : "igv2-avatar igv2-avatar--brand";
  return <div className={className}>{getInitial(name)}</div>;
}

function pickPrimaryAttachment(
  attachments: ConversationAttachment[],
): ConversationAttachment | null {
  if (!attachments.length) return null;
  return (
    attachments.find((item) => item.type === "image" && item.url) ||
    attachments.find((item) => item.url) ||
    attachments[0]
  );
}

function isRenderableImageAttachment(
  attachment: ConversationAttachment | null,
): boolean {
  if (!attachment?.url) return false;
  if (attachment.type === "image") return true;
  if (attachment.mime_type?.startsWith("image/")) return true;
  if (/\.gif($|\?)/i.test(attachment.url)) return true;
  return attachment.url.includes("lookaside.fbsbx.com/ig_messaging_cdn/");
}

export const InstagramV2Page = () => {
  const { pick } = useUiLanguage();
  const t = pick;
  const [tab, setTab] = useState<TabKey>("dashboard");
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null);
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailInfoVisible, setDetailInfoVisible] = useState(true);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [previewFrameKind, setPreviewFrameKind] = useState<PreviewFrameKind>("portrait");
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerInitialTitle, setComposerInitialTitle] = useState("");
  const [composerInitialCaption, setComposerInitialCaption] = useState("");
  const [busyOverlay, setBusyOverlay] = useState<string | null>(null);
  const [postRefreshing, setPostRefreshing] = useState(false);
  const [postDetailComments, setPostDetailComments] = useState<Record<string, ApiCommentItem[]>>({});
  const [commentMediaPosts, setCommentMediaPosts] = useState<Record<string, UiPost>>({});
  const [cacheRefreshing, setCacheRefreshing] = useState(false);
  const [autoRefreshStarted, setAutoRefreshStarted] = useState(false);
  const [temporaryCommentPostId, setTemporaryCommentPostId] = useState<string | null>(null);
  const [commentReplyTarget, setCommentReplyTarget] = useState<CommentReplyTarget | null>(null);
  const [commentReplyBody, setCommentReplyBody] = useState("");
  const [commentReplySending, setCommentReplySending] = useState(false);
  const [commentComposeBody, setCommentComposeBody] = useState("");
  const [commentComposeSending, setCommentComposeSending] = useState(false);
  const [inboxFilter, setInboxFilter] = useState<InboxFilterKey>("all");
  const [dmReplyText, setDmReplyText] = useState("");
  const [dmReplySending, setDmReplySending] = useState(false);
  const [dmAttachment, setDmAttachment] = useState<{ file: File; previewUrl: string } | null>(null);
  const [dmLastSyncAt, setDmLastSyncAt] = useState<number | null>(null);
  const [optimisticReadMarks, setOptimisticReadMarks] = useState<Record<string, string>>({});
  const dmAttachmentInputRef = useRef<HTMLInputElement | null>(null);
  const dmAttachmentPreviewUrlRef = useRef<string | null>(null);
  const dmListRef = useRef<HTMLDivElement | null>(null);
  const dmBubblesRef = useRef<HTMLDivElement | null>(null);
  const commentsListRef = useRef<HTMLDivElement | null>(null);
  const commentStreamRef = useRef<HTMLDivElement | null>(null);
  const contentListRef = useRef<HTMLDivElement | null>(null);
  const contentLoadMoreRef = useRef<HTMLDivElement | null>(null);
  const dmSeenMessageStateRef = useRef<Record<string, { count: number; latestKey: string | null }>>({});
  const dmScrollRestoreRef = useRef<{ height: number; top: number } | null>(null);

  const dashboard = useInstagramDashboard();
  const connection = useInstagramConnection();
  const conversations = useInstagramConversations({ limit: 10 });
  const conversationDetail = useInstagramConversationWindow(activeConversationId, { limit: 10 });
  const comments = useInstagramComments({ limit: 10 });
  const media = useInstagramMedia({ limit: 3 });
  const drafts = useInstagramDrafts();
  const workspaceMediaId = useMemo(() => {
    if (temporaryCommentPostId) return temporaryCommentPostId;
    if (!activeCommentId) return null;
    const activeThread = (comments.data?.items ?? []).find(
      (item) => item.id === activeCommentId || item.replies.some((reply) => reply.id === activeCommentId),
    );
    return activeThread?.media_id ?? null;
  }, [activeCommentId, comments.data, temporaryCommentPostId]);
  const commentWorkspace = useInstagramComments({
    mediaId: workspaceMediaId ?? undefined,
    limit: 10,
    enabled: Boolean(workspaceMediaId),
  });

  const tabs = useMemo<Array<{ key: TabKey; label: string; icon: "dashboard" | "inbox" | "comments" | "content" }>>(
    () => [
      { key: "dashboard", label: t("ダッシュボード", "Dashboard"), icon: "dashboard" },
      { key: "content", label: t("投稿", "Posts"), icon: "content" },
      { key: "comments", label: t("コメント", "Comments"), icon: "comments" },
      { key: "inbox", label: t("DM管理", "DM"), icon: "inbox" },
    ],
    [t],
  );

  const statusLabel = useMemo<Record<string, string>>(
    () => ({
      pending: t("未対応", "Pending"),
      in_progress: t("対応中", "In progress"),
      done: t("完了", "Done"),
      spam: t("スパム", "Spam"),
      replied: t("返信済み", "Replied"),
      published: t("公開済み", "Published"),
      scheduled: t("予約済み", "Scheduled"),
      draft: t("下書き", "Draft"),
      failed: t("失敗", "Failed"),
      publishing: t("公開中", "Publishing"),
    }),
    [t],
  );

  const reloadInstagramViews = async (targets?: string[]) => {
    const pending: Promise<unknown>[] = [];
    if (!targets || targets.includes("dashboard")) dashboard.refresh();
    if (!targets || targets.includes("conversations")) {
      pending.push(conversations.refreshLatest());
      if (activeConversationId) {
        pending.push(conversationDetail.refreshLatest());
      }
    }
    if (!targets || targets.includes("comments")) {
      pending.push(comments.refreshLatest());
      if (workspaceMediaId) {
        pending.push(commentWorkspace.refreshLatest());
      }
    }
    if (!targets || targets.includes("media")) pending.push(media.refreshLatest());
    if (!targets) drafts.refresh();
    await Promise.all(pending);
  };

  const runCacheRefresh = async (force = false, targets?: string[]) => {
    if (cacheRefreshing) return false;
    setCacheRefreshing(true);
    try {
      await instagramApi.refreshCache({
        targets,
        force,
      });
      if (targets?.includes("conversations")) {
        setDmLastSyncAt(Date.now());
      }
      await reloadInstagramViews(targets);
      return true;
    } catch (e) {
      if (force) {
        window.alert(e instanceof Error ? e.message : t("Instagram データの更新に失敗しました。", "Failed to refresh Instagram data."));
      }
      return false;
    } finally {
      setCacheRefreshing(false);
    }
  };

  const startCommentReply = (parentCommentId: string, targetUserName: string) => {
    setCommentReplyTarget({ parentCommentId, targetUserName });
    setCommentReplyBody("");
  };

  const commentReplyPrefix = commentReplyTarget ? `@${commentReplyTarget.targetUserName} ` : "";
  const commentReplyValue = `${commentReplyPrefix}${commentReplyBody}`;

  const handleCommentReplyChange = (value: string) => {
    if (!commentReplyTarget) {
      setCommentReplyBody(value);
      return;
    }
    if (value.startsWith(commentReplyPrefix)) {
      setCommentReplyBody(value.slice(commentReplyPrefix.length));
      return;
    }
    setCommentReplyBody(value.replace(/^@\S+\s*/, ""));
  };

  const sendCommentReply = async () => {
    if (!commentReplyTarget || commentReplySending) return;
    const body = commentReplyBody.trim();
    if (!body) return;
    setCommentReplySending(true);
    try {
      await instagramApi.replyComment(commentReplyTarget.parentCommentId, {
        text: `${commentReplyPrefix}${body}`,
      });
      setCommentReplyBody("");
      setCommentReplyTarget(null);
      comments.refresh();
      if (workspaceMediaId) {
        commentWorkspace.refreshLatest();
      }
      dashboard.refresh();
      media.refresh();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : t("コメント返信に失敗しました。", "Failed to reply to the comment."));
    } finally {
      setCommentReplySending(false);
    }
  };

  const openCommentsWorkspaceForPost = (post: UiPost) => {
    setSelectedPostId(post.id);
    setGalleryIndex(0);
    setDetailOpen(false);
    setTab("comments");
    setCommentReplyTarget(null);
    setCommentReplyBody("");
    setCommentComposeBody("");
    setTemporaryCommentPostId(post.id);

    const firstThread = commentThreads.find((item) => item.mediaId === post.id);
    if (firstThread) {
      setActiveCommentId(firstThread.id);
      return;
    }

    setActiveCommentId(null);
    setTemporaryCommentPostId(post.id);
  };

  const sendPostComment = async (mediaId: string) => {
    if (commentComposeSending) return;
    const body = commentComposeBody.trim();
    if (!body) return;

    setCommentComposeSending(true);
    try {
      const created = await instagramApi.createMediaComment(mediaId, { text: body }) as {
        ok: boolean;
        comment?: { id?: string };
      };
      setCommentComposeBody("");
      setTemporaryCommentPostId(mediaId);
      if (created.comment?.id) {
        setActiveCommentId(created.comment.id);
      }
      comments.refresh();
      if (workspaceMediaId || mediaId) {
        commentWorkspace.refreshLatest();
      }
      dashboard.refresh();
      media.refresh();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : t("コメント投稿に失敗しました。", "Failed to post the comment."));
    } finally {
      setCommentComposeSending(false);
    }
  };

  const conversationMessages = conversationDetail.data?.messages ?? [];
  const sortedConversationMessages = useMemo(
    () => sortMessagesByCreatedAt(conversationMessages),
    [conversationMessages],
  );

  const uiConversations = useMemo(
    () => (conversations.data?.items ?? []).map((item) => {
      const mapped = mapConversation(item, t);
      if (mapped.id === activeConversationId && sortedConversationMessages.length > 0) {
        const activeLatest = sortedConversationMessages[sortedConversationMessages.length - 1];
        mapped.preview = messagePreview(activeLatest.text || "", activeLatest.attachments || [], t);
        mapped.time = formatRelativeTime(activeLatest.created_at);
        mapped.updatedAt = activeLatest.created_at;
      }
      return {
        ...mapped,
        isRead: mapped.isRead || optimisticReadMarks[mapped.id] === mapped.updatedAt,
      };
    }),
    [activeConversationId, conversations.data, optimisticReadMarks, sortedConversationMessages, t],
  );
  const pendingInboxCount = conversations.data?.counts?.pending
    ?? uiConversations.filter((item) => item.status === "pending").length;
  const importantInboxCount = uiConversations.filter((item) => item.unread > 0 || item.status === "in_progress").length;
  const visibleConversations = uiConversations.filter((item) => {
    if (inboxFilter === "pending") return item.status === "pending";
    if (inboxFilter === "important") return item.unread > 0 || item.status === "in_progress";
    return true;
  });
  const commentThreads = useMemo(
    () => (comments.data?.items ?? []).map(mapCommentThread),
    [comments.data],
  );
  const uiComments = useMemo(
    () => flattenComments(comments.data?.items ?? [], t),
    [comments.data, t],
  );
  const publishedPosts = useMemo(
    () => (media.data?.items ?? []).map(mapMediaPost),
    [media.data],
  );
  const draftPosts = useMemo(
    () => (drafts.data?.items ?? [])
      .filter((item) => item.status !== "published")
      .map(mapDraftPost),
    [drafts.data],
  );
  const allContentPosts = useMemo(
    () => [...draftPosts, ...publishedPosts],
    [draftPosts, publishedPosts],
  );
  const mediaPostsWithCommentCount = useMemo(
    () => (media.data?.items ?? []).filter((item) => (item.comments_count ?? 0) > 0),
    [media.data],
  );
  const hiddenCommentDiagnostics = comments.data?.diagnostics?.media_with_hidden_comments ?? [];
  const hasCommentCountMismatch =
    uiComments.length === 0 &&
    (hiddenCommentDiagnostics.length > 0 || mediaPostsWithCommentCount.length > 0);
  const visibleComments = commentThreads;

  useEffect(() => {
    if (!activeConversationId && visibleConversations[0]) {
      setActiveConversationId(visibleConversations[0].id);
    }
  }, [activeConversationId, visibleConversations]);

  useEffect(() => {
    if (activeConversationId && visibleConversations.some((item) => item.id === activeConversationId)) return;
    setActiveConversationId(visibleConversations[0]?.id ?? null);
  }, [activeConversationId, visibleConversations]);

  useEffect(() => {
    if (!activeCommentId && visibleComments[0]) {
      setActiveCommentId(visibleComments[0].id);
    }
  }, [activeCommentId, visibleComments]);

  useEffect(() => {
    if (activeCommentId && visibleComments.some((item) => item.id === activeCommentId)) return;
    setActiveCommentId(visibleComments[0]?.id ?? null);
  }, [activeCommentId, visibleComments]);

  useEffect(() => {
    if (tab === "comments") return;
    setTemporaryCommentPostId(null);
    setCommentReplyTarget(null);
    setCommentReplyBody("");
    setCommentComposeBody("");
  }, [tab]);

  useEffect(() => {
    if (!selectedPostId && allContentPosts[0]) {
      setSelectedPostId(allContentPosts[0].id);
    }
  }, [allContentPosts, selectedPostId]);

  const syncMetas = [
    dashboard.data?.sync_meta,
    conversations.data?.sync_meta,
    comments.data?.sync_meta,
    media.data?.sync_meta,
  ];
  const hasStaleCache = syncMetas.some((meta) => meta?.stale);

  useEffect(() => {
    const node = dmListRef.current;
    if (!node) return;
    const handleScroll = () => {
      if (node.scrollTop + node.clientHeight < node.scrollHeight - 80) return;
      conversations.loadMore();
    };
    node.addEventListener("scroll", handleScroll);
    return () => node.removeEventListener("scroll", handleScroll);
  }, [conversations]);

  useEffect(() => {
    const node = dmBubblesRef.current;
    if (!node) return;
    const handleScroll = () => {
      if (node.scrollTop > 80) return;
      if (!conversationDetail.hasOlder || conversationDetail.loadingOlder) return;
      dmScrollRestoreRef.current = {
        height: node.scrollHeight,
        top: node.scrollTop,
      };
      conversationDetail.loadOlder();
    };
    node.addEventListener("scroll", handleScroll);
    return () => node.removeEventListener("scroll", handleScroll);
  }, [conversationDetail]);

  useEffect(() => {
    const node = commentsListRef.current;
    if (!node) return;
    const handleScroll = () => {
      if (node.scrollTop + node.clientHeight < node.scrollHeight - 80) return;
      if (temporaryCommentPostId) {
        commentWorkspace.loadMore();
        return;
      }
      comments.loadMore();
    };
    node.addEventListener("scroll", handleScroll);
    return () => node.removeEventListener("scroll", handleScroll);
  }, [commentWorkspace, comments, temporaryCommentPostId]);

  useEffect(() => {
    const node = commentStreamRef.current;
    if (!node) return;
    const handleScroll = () => {
      if (node.scrollTop + node.clientHeight < node.scrollHeight - 80) return;
      commentWorkspace.loadMore();
    };
    node.addEventListener("scroll", handleScroll);
    return () => node.removeEventListener("scroll", handleScroll);
  }, [commentWorkspace]);

  useEffect(() => {
    const node = contentListRef.current;
    if (!node) return;
    const handleScroll = () => {
      if (node.scrollTop + node.clientHeight < node.scrollHeight - 80) return;
      media.loadMore();
    };
    node.addEventListener("scroll", handleScroll);
    return () => node.removeEventListener("scroll", handleScroll);
  }, [media]);

  useEffect(() => {
    if (tab !== "content") return;
    const node = contentListRef.current;
    if (!node) return;
    if (media.loading || media.loadingMore || !media.hasMore) return;
    if (node.scrollHeight > node.clientHeight + 8) return;
    media.loadMore();
  }, [tab, media.data?.items.length, media.hasMore, media.loading, media.loadingMore, media]);

  useEffect(() => {
    if (tab !== "content") return;
    const root = contentListRef.current;
    const target = contentLoadMoreRef.current;
    if (!root || !target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (media.loading || media.loadingMore || !media.hasMore) return;
        media.loadMore();
      },
      {
        root,
        rootMargin: "0px 0px 160px 0px",
        threshold: 0.1,
      },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [tab, media.data?.items.length, media.hasMore, media.loading, media.loadingMore, media]);

  useEffect(() => {
    if (!workspaceMediaId) return;
    if (allContentPosts.some((item) => item.id === workspaceMediaId)) return;
    if (commentMediaPosts[workspaceMediaId]) return;

    let cancelled = false;
    void instagramApi.refreshMedia(workspaceMediaId)
      .then((item) => {
        if (cancelled) return;
        setCommentMediaPosts((current) => ({
          ...current,
          [item.id]: mapMediaPost(item),
        }));
      })
      .catch(() => {
        // コメント一覧は表示できるため、投稿詳細の補完失敗だけでは画面全体を止めない。
      });

    return () => {
      cancelled = true;
    };
  }, [allContentPosts, commentMediaPosts, workspaceMediaId]);

  const handleConversationSelect = async (item: UiConversation) => {
    setActiveConversationId(item.id);
    setDmReplyText("");
    if (dmAttachmentPreviewUrlRef.current) {
      URL.revokeObjectURL(dmAttachmentPreviewUrlRef.current);
      dmAttachmentPreviewUrlRef.current = null;
    }
    setDmAttachment(null);
    if (dmAttachmentInputRef.current) dmAttachmentInputRef.current.value = "";
    if (item.isRead) return;

    setOptimisticReadMarks((prev) => ({
      ...prev,
      [item.id]: item.updatedAt,
    }));

    try {
      setBusyOverlay(t("会話を更新中...", "Updating conversation..."));
      await instagramApi.markConversationRead(item.id);
      dashboard.refresh();
      conversations.refresh();
      conversationDetail.refreshLatest();
    } catch (e) {
      setOptimisticReadMarks((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      window.alert(e instanceof Error ? e.message : t("既読更新に失敗しました。", "Failed to mark the conversation as read."));
    } finally {
      setBusyOverlay(null);
    }
  };

  const sendDmReply = async () => {
    const conversationId = activeConversation?.id;
    const text = dmReplyText.trim();
    const attachment = dmAttachment;
    if (!conversationId || (!text && !attachment) || dmReplySending) return;

    setDmReplySending(true);
    try {
      let imageUrl: string | undefined;
      if (attachment) {
        const uploaded = await uploadMedia({
          file: attachment.file,
          title: attachment.file.name,
          alt_text: attachment.file.name,
        });
        imageUrl = uploaded.source_url || uploaded.thumbnail_url || undefined;
        if (!imageUrl) {
          throw new Error(t("アップロードした画像 URL を取得できませんでした。", "Could not read the uploaded image URL."));
        }
      }
      await instagramApi.sendConversationMessage(conversationId, {
        text: text || undefined,
        image_url: imageUrl,
      });
      setDmReplyText("");
      if (attachment) {
        URL.revokeObjectURL(attachment.previewUrl);
        dmAttachmentPreviewUrlRef.current = null;
      }
      setDmAttachment(null);
      conversations.refresh();
      conversationDetail.refreshLatest();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : t("DM返信に失敗しました。", "Failed to send the DM."));
    } finally {
      setDmReplySending(false);
    }
  };

  const chooseDmAttachment = (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      window.alert(t("画像ファイルを選択してください。", "Please choose an image file."));
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    if (dmAttachmentPreviewUrlRef.current) URL.revokeObjectURL(dmAttachmentPreviewUrlRef.current);
    dmAttachmentPreviewUrlRef.current = previewUrl;
    setDmAttachment({ file, previewUrl });
  };

  const clearDmAttachment = () => {
    if (dmAttachmentPreviewUrlRef.current) URL.revokeObjectURL(dmAttachmentPreviewUrlRef.current);
    dmAttachmentPreviewUrlRef.current = null;
    setDmAttachment(null);
    if (dmAttachmentInputRef.current) dmAttachmentInputRef.current.value = "";
  };

  useEffect(() => {
    return () => {
      if (dmAttachmentPreviewUrlRef.current) {
        URL.revokeObjectURL(dmAttachmentPreviewUrlRef.current);
        dmAttachmentPreviewUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (autoRefreshStarted) return;
    if (
      dashboard.loading ||
      conversations.loading ||
      comments.loading ||
      media.loading
    ) {
      return;
    }
    if (!hasStaleCache) return;
    setAutoRefreshStarted(true);
    void runCacheRefresh(false, ["dashboard", "conversations", "comments", "media"]);
  }, [
    autoRefreshStarted,
    comments.loading,
    conversations.loading,
    dashboard.loading,
    hasStaleCache,
    media.loading,
  ]);

  const dmLastSyncLabel = useMemo(() => {
    if (!dmLastSyncAt) return null;
    return t("最終更新 ", "Last updated ") + new Date(dmLastSyncAt).toLocaleTimeString("ja-JP", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }, [dmLastSyncAt, t]);

  const workspaceCommentThreads = useMemo(
    () => (commentWorkspace.data?.items ?? []).map(mapCommentThread),
    [commentWorkspace.data],
  );
  const activePostCommentThreads = useMemo(() => {
    if (!workspaceMediaId) return [] as UiCommentThread[];
    return workspaceCommentThreads;
  }, [workspaceCommentThreads, workspaceMediaId]);
  const commentOverviewItems = temporaryCommentPostId ? activePostCommentThreads : visibleComments;
  const commentOverviewLoading = temporaryCommentPostId
    ? commentWorkspace.loading || commentWorkspace.refreshingLatest
    : comments.loading || comments.refreshingLatest;
  const commentOverviewLoadingMore = temporaryCommentPostId
    ? commentWorkspace.loadingMore
    : comments.loadingMore;
  const activeConversation = uiConversations.find((item) => item.id === activeConversationId) ?? uiConversations[0] ?? null;
  const activeComment = commentOverviewItems.find((item) => item.id === activeCommentId)
    ?? activePostCommentThreads.find((item) => item.id === activeCommentId)
    ?? visibleComments.find((item) => item.id === activeCommentId)
    ?? (temporaryCommentPostId ? activePostCommentThreads[0] : visibleComments[0])
    ?? null;
  const recentMediaSummary = dashboard.data?.recent_media_summary ?? null;
  const dashboardTopLikedPost = recentMediaSummary?.top_liked ?? null;
  const dashboardTopCommentedPost = recentMediaSummary?.top_commented ?? null;
  const summarySelectedPost = [dashboardTopLikedPost, dashboardTopCommentedPost]
    .filter((item): item is RecentMediaSummaryItem => Boolean(item?.id))
    .find((item) => item.id === selectedPostId);
  const temporaryCommentPost = temporaryCommentPostId
    ? allContentPosts.find((item) => item.id === temporaryCommentPostId) ?? null
    : null;
  const selectedPost = allContentPosts.find((item) => item.id === selectedPostId)
    ?? (summarySelectedPost ? mapSummaryMediaPost(summarySelectedPost, t) : null)
    ?? allContentPosts[0]
    ?? null;
  const galleryImage = selectedPost?.images[galleryIndex] ?? selectedPost?.images[0] ?? FALLBACK_IMAGE;
  const selectedPostIsVideo = Boolean(
    selectedPost
    && selectedPost.source === "media"
    && (selectedPost.mediaType === "VIDEO" || selectedPost.mediaType === "REEL")
    && selectedPost.videoUrl,
  );
  const previewBackdropImage = selectedPost?.thumb || galleryImage;

  const classifyPreviewFrame = useCallback((width: number, height: number) => {
    if (!width || !height) return;
    const ratio = width / height;
    if (ratio <= 0.6) {
      setPreviewFrameKind("tall");
      return;
    }
    if (ratio < 0.95) {
      setPreviewFrameKind("portrait");
      return;
    }
    if (ratio <= 1.05) {
      setPreviewFrameKind("square");
      return;
    }
    setPreviewFrameKind("landscape");
  }, []);

  useEffect(() => {
    if (!detailOpen) return;
    setDetailInfoVisible(true);
    setPreviewFrameKind("portrait");
  }, [detailOpen, selectedPostId]);
  const messageGroups = splitMessagesByDay(sortedConversationMessages);
  const latestMessage = sortedConversationMessages[sortedConversationMessages.length - 1] ?? null;
  const latestMessageKey = activeConversationId && latestMessage
    ? `${activeConversationId}:${latestMessage.id}:${latestMessage.created_at}`
    : activeConversationId
      ? `${activeConversationId}:empty`
      : null;

  useLayoutEffect(() => {
    const restore = dmScrollRestoreRef.current;
    const container = dmBubblesRef.current;
    const seenState = activeConversationId
      ? dmSeenMessageStateRef.current[activeConversationId]
      : null;
    if (!restore || !container || !seenState || latestMessageKey !== seenState.latestKey) {
      dmScrollRestoreRef.current = null;
      return;
    }
    container.scrollTop = restore.top + (container.scrollHeight - restore.height);
    dmScrollRestoreRef.current = null;
  }, [conversationMessages.length, latestMessageKey]);

  useEffect(() => {
    dmScrollRestoreRef.current = null;
  }, [activeConversationId]);

  useLayoutEffect(() => {
    if (tab !== "inbox") return;
    if (!activeConversationId || !latestMessageKey) return;

    const previous = dmSeenMessageStateRef.current[activeConversationId];
    const current = {
      count: conversationMessages.length,
      latestKey: latestMessageKey,
    };
    const shouldScrollToLatest = !previous
      || (
        current.count > previous.count
        && current.latestKey !== previous.latestKey
      )
      || (
        current.latestKey !== previous.latestKey
        && current.count >= previous.count
        && latestMessage?.direction === "inbound"
      );

    dmSeenMessageStateRef.current[activeConversationId] = current;
    if (!shouldScrollToLatest) return;

    const container = dmBubblesRef.current;
    if (!container) return;

    let cancelled = false;

    const waitForMediaReady = async () => {
      const mediaNodes = Array.from(container.querySelectorAll("img"));
      if (!mediaNodes.length) return;
      await Promise.all(
        mediaNodes.map(async (node) => {
          if (node.complete) return;
          if (typeof node.decode === "function") {
            try {
              await node.decode();
              return;
            } catch {
              return;
            }
          }
          await new Promise<void>((resolve) => {
            const done = () => resolve();
            node.addEventListener("load", done, { once: true });
            node.addEventListener("error", done, { once: true });
          });
        }),
      );
    };

    const run = async () => {
      await waitForMediaReady();
      if (cancelled) return;
      container.scrollTop = container.scrollHeight;
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [activeConversationId, conversationMessages.length, latestMessage?.direction, latestMessageKey, tab]);
  const selectedPostComments = useMemo<UiPostCommentDetail[]>(() => {
    if (!selectedPost) return [];
    const targetId = selectedPost.source === "media" ? selectedPost.id : null;
    if (!targetId) return [];

    const detailItems = postDetailComments[targetId];
    const items = detailItems ?? comments.data?.items ?? [];
    return items
      .filter((item) => item.media_id === targetId)
      .flatMap((item) => {
        const topLevel: UiPostCommentDetail = {
          id: item.id,
          author: resolveCommentAuthor(item.user_name, t),
          comment: item.text,
          time: formatRelativeTime(item.created_at),
          status: item.replies.length > 0 ? "replied" : item.status,
          isReply: false,
          parentAuthor: null,
        };
        const replies = item.replies.map<UiPostCommentDetail>((reply) => ({
          id: reply.id,
          author: resolveCommentAuthor(reply.user_name, t),
          comment: reply.text,
          time: formatRelativeTime(reply.created_at),
          status: "replied",
          isReply: true,
          parentAuthor: resolveCommentAuthor(item.user_name, t),
        }));
        return [topLevel, ...replies];
      });
  }, [comments.data, postDetailComments, selectedPost, t]);
  const dashboardRecentContent = useMemo(
    () => allContentPosts.slice(0, 3),
    [allContentPosts],
  );

  const draftCount = drafts.data?.items.filter((item) => item.status === "draft").length ?? 0;
  const scheduledCount = drafts.data?.items.filter((item) => item.status === "scheduled").length ?? 0;

  const dashboardStatusCards = useMemo(() => {
    const metrics = dashboard.data?.metrics;
    return [
      {
        key: "dm",
        label: t("待返信 DM", "Pending DMs"),
        value: formatNumber(metrics?.pending_dm ?? null),
        note: t("いま対応が必要", "Needs attention now"),
        icon: <Send size={18} />,
        toneClass: "tone-violet",
      },
      {
        key: "comments",
        label: t("待返信コメント", "Pending comments"),
        value: formatNumber(metrics?.pending_comments ?? null),
        note: t("返信待ちの投稿コメント", "Comments waiting for reply"),
        icon: <MessageCircle size={18} />,
        toneClass: "tone-pink",
      },
      {
        key: "drafts",
        label: t("下書き", "Drafts"),
        value: formatNumber(draftCount),
        note: t("投稿ページだけで管理", "Managed in Posts only"),
        icon: <ImageIcon size={18} />,
        toneClass: "tone-orange",
      },
      {
        key: "followers",
        label: t("フォロワー", "Followers"),
        value: formatNumber(dashboard.data?.metrics.followers ?? null),
        note: t("接続アカウントの現在値", "Current account total"),
        icon: <Users size={18} />,
        toneClass: "tone-mint",
      },
    ];
  }, [dashboard.data?.metrics, draftCount, t]);

  const contentSummary = {
    total: dashboard.data?.account?.media_count ?? media.data?.total ?? media.data?.items.length ?? 0,
    scheduled: scheduledCount,
    draft: draftCount,
  };

  const activeConversationParticipant = conversationDetail.data?.participant;
  const activeCommentDetail = useMemo(() => {
    const items = [
      ...(comments.data?.items ?? []),
      ...(commentWorkspace.data?.items ?? []),
    ];
    for (const item of items) {
      if (item.id === activeCommentId) return item;
      if (item.replies.some((reply) => reply.id === activeCommentId)) return item;
    }
    return null;
  }, [activeCommentId, commentWorkspace.data, comments.data]);
  const activeCommentMediaId = temporaryCommentPostId ?? activeComment?.mediaId ?? null;
  const activeCommentPost = temporaryCommentPost
    ?? (activeCommentMediaId
      ? publishedPosts.find((item) => item.id === activeCommentMediaId)
        ?? commentMediaPosts[activeCommentMediaId]
        ?? null
      : null);
  const resolveCommentPostTitle = useCallback((item: UiCommentThread) => {
    const loadedPost = allContentPosts.find((post) => post.id === item.mediaId)
      ?? commentMediaPosts[item.mediaId]
      ?? null;
    if (loadedPost?.title) return loadedPost.title;
    if (item.mediaTitle && item.mediaTitle !== "Instagram 投稿") return item.mediaTitle;
    return t("Instagram 投稿", "Instagram post");
  }, [allContentPosts, commentMediaPosts, t]);
  const activePostCommentTotal = useMemo(
    () => activeCommentPost?.comments ?? activePostCommentThreads.reduce((sum, item) => sum + 1 + item.replies.length, 0),
    [activeCommentPost?.comments, activePostCommentThreads],
  );
  const activePostComments = workspaceMediaId
    ? uiComments.filter((item) => item.mediaId === workspaceMediaId)
    : [];
  const commentComposeMediaId = workspaceMediaId ?? (activeComment?.mediaId || null);
  const commentDockValue = commentReplyTarget ? commentReplyValue : commentComposeBody;
  const commentDockPlaceholder = commentReplyTarget
    ? t("返信内容を入力...", "Type your reply...")
    : commentComposeMediaId
      ? t("この投稿へコメントを入力...", "Comment on this post...")
      : t("返信したいコメントで「返信」を押してください", "Select Reply on a comment first");
  const commentDockSending = commentReplyTarget ? commentReplySending : commentComposeSending;
  const commentDockModeLabel = commentReplyTarget
    ? t("回复给", "Replying to")
    : t("发布到该帖子", "Commenting on this post");

  const handleComposerSaved = () => {
    drafts.refresh();
    void runCacheRefresh(true, ["media", "dashboard"]);
  };

  const openComposerForCreate = () => {
    setComposerInitialTitle("");
    setComposerInitialCaption("");
    setComposerOpen(true);
  };

  const handleDeleteSelectedPost = async () => {
    if (!selectedPost) return;
    const confirmMessage = selectedPost.source === "draft"
      ? t("この下書き / 失敗投稿を削除します。よろしいですか?", "Delete this draft / failed post?")
      : t("この公開済み投稿を削除します。よろしいですか?", "Delete this published post?");
    if (!window.confirm(confirmMessage)) return;
    try {
      setBusyOverlay(t("削除中...", "Deleting..."));
      if (selectedPost.source === "draft") {
        await instagramApi.deleteDraft(selectedPost.id);
      } else {
        await instagramApi.deleteMedia(selectedPost.id);
      }
      setDetailOpen(false);
      setSelectedPostId(null);
      drafts.refresh();
      media.refresh();
      comments.refresh();
      commentWorkspace.refresh();
      dashboard.refresh();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : t("削除に失敗しました。", "Failed to delete."));
    } finally {
      setBusyOverlay(null);
    }
  };

  const handleRefreshSelectedPost = async () => {
    if (!selectedPost || selectedPost.source !== "media" || postRefreshing) return;
    try {
      setPostRefreshing(true);
      const refreshed = await instagramApi.refreshMedia(selectedPost.id);
      const latestComments = await instagramApi.listComments({
        mediaId: selectedPost.id,
        limit: 10,
      });
      setSelectedPostId(refreshed.id);
      media.replaceItem(refreshed);
      setPostDetailComments((current) => ({
        ...current,
        [selectedPost.id]: latestComments.items,
      }));
      comments.refreshLatest();
      commentWorkspace.refresh();
      dashboard.refresh();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : t("投稿の更新に失敗しました。", "Failed to refresh post."));
    } finally {
      setPostRefreshing(false);
    }
  };

  const handlePublishDraft = async (draftId: string) => {
    try {
      setBusyOverlay(t("公開中...", "Publishing..."));
      await instagramApi.publishDraft(draftId);
      setDetailOpen(false);
      setSelectedPostId(null);
      drafts.refresh();
      media.refresh();
      comments.refresh();
      commentWorkspace.refresh();
      dashboard.refresh();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : t("公開に失敗しました。", "Failed to publish draft."));
    } finally {
      setBusyOverlay(null);
    }
  };

  return (
    <div className="igv2-page">
      {busyOverlay ? (
        <div className="igv2-busy-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="igv2-busy-card">
            <Loader2 size={20} className="igv2-spin" />
            <span>{busyOverlay}</span>
          </div>
        </div>
      ) : null}
      <section className={`igv2-shell ${tab === "inbox" ? "igv2-shell--inbox" : ""} ${tab === "comments" ? "igv2-shell--comments" : ""}`}>
        <div className="igv2-hero">
          <div className="igv2-hero-main">
            <div className="igv2-tabbar">
              {tabs.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`igv2-tab ${tab === item.key ? "active" : ""}`}
                  onClick={() => setTab(item.key)}
                >
                  {item.icon === "dashboard" ? <BarChart3 size={13} strokeWidth={2.1} /> : null}
                  {item.icon === "inbox" ? <Send size={13} strokeWidth={2.1} /> : null}
                  {item.icon === "comments" ? <MessageCircle size={13} strokeWidth={2.1} /> : null}
                  {item.icon === "content" ? <ImageIcon size={13} strokeWidth={2.1} /> : null}
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          <div className="igv2-hero-actions">
            <button className="igv2-chip igv2-chip--light" type="button">
              <Circle size={8} fill="currentColor" />
              {t("接続アカウント", "Connected account")}: {dashboard.data?.account?.username ? `@${dashboard.data.account.username}` : connection.data?.connection?.account_name || t("未接続", "Not connected")}
            </button>
            <button
              className="igv2-chip igv2-chip--light"
              type="button"
              onClick={() => {
                if (tab === "inbox") {
                  void runCacheRefresh(true, ["conversations"]);
                  return;
                }
                void runCacheRefresh(true);
              }}
              disabled={cacheRefreshing}
            >
              <Sparkles size={14} />
              {cacheRefreshing ? t("更新中...", "Refreshing...") : t("今すぐ更新", "Refresh now")}
            </button>
            {tab === "inbox" && dmLastSyncLabel ? (
              <div className="igv2-auto-refresh-note">
                <span>{dmLastSyncLabel}</span>
              </div>
            ) : null}
          </div>
        </div>

        <div className={`igv2-body ${tab === "inbox" ? "igv2-body--inbox" : ""} ${tab === "comments" ? "igv2-body--comments" : ""}`}>
          {tab === "dashboard" && (
            <div className="igv2-dashboard-minimal">
              <div className="igv2-dashboard-g-grid">
                <section className="igv2-panel igv2-panel--album">
                  <div className="igv2-panel-head">
                    <div>
                      <h3>{t("最近 3 件の内容", "Latest 3 items")}</h3>
                      <p>{t("左で内容、右で運用状態を確認します", "Content on the left, operations on the right")}</p>
                    </div>
                  </div>
                  <div className="igv2-highlight-grid">
                    {dashboardRecentContent.map((post) => (
                      <button
                        key={post.id}
                        type="button"
                        className="igv2-highlight-card"
                        onClick={() => {
                          setSelectedPostId(post.id);
                          setGalleryIndex(0);
                          setDetailOpen(true);
                        }}
                      >
                        <img src={post.thumb || FALLBACK_IMAGE} alt={post.title} />
                        <span className="igv2-highlight-badge">1/{post.images.length}</span>
                        <span className={`igv2-status-chip status-${post.status}`}>
                          {post.status === "draft"
                            ? t("下書き", "Draft")
                            : post.status === "scheduled"
                              ? t("予約", "Scheduled")
                              : t("公開済み", "Published")}
                        </span>
                        <div className="igv2-highlight-meta">
                          <strong>{post.title}</strong>
                          <span>{post.date}</span>
                          {post.source === "media" ? (
                            <div className="igv2-highlight-stats">
                              <em><Heart size={14} /> {formatNumber(post.likes)}</em>
                              <em><MessageCircle size={14} /> {formatNumber(post.comments)}</em>
                            </div>
                          ) : (
                            <div className="igv2-highlight-stats">
                              <em><ImageIcon size={14} /> {formatNumber(post.images.length)}</em>
                              <em>{t("公開前", "Pre-publish")}</em>
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </section>

                <section className="igv2-panel igv2-dashboard-g-side">
                  <div className="igv2-panel-head">
                    <div>
                      <h3>{t("運用状態", "Operations status")}</h3>
                      <p>{t("今の対応量をまとめて確認できます", "A compact view of what needs attention")}</p>
                    </div>
                  </div>
                  <div className="igv2-status-grid">
                    {dashboardStatusCards.map((item) => (
                      <article key={item.key} className="igv2-status-card">
                        <span className={`igv2-action-dot ${item.toneClass}`}>{item.icon}</span>
                        <strong>{item.value}</strong>
                        <span>{item.label}</span>
                        <em>{item.note}</em>
                      </article>
                    ))}
                  </div>
                </section>
              </div>

              <section className="igv2-panel igv2-dashboard-summary-panel">
                <div className="igv2-panel-head">
                  <div>
                    <h3>{t("表现摘要", "Performance summary")}</h3>
                    <p>{t("最近 15 件の公開投稿だけで軽く見ます", "A light summary based on your latest 15 published posts")}</p>
                  </div>
                </div>
                <div className="igv2-dashboard-summary-list">
                  <button
                    type="button"
                    className="igv2-dashboard-summary-row igv2-dashboard-summary-row--button"
                    disabled={!dashboardTopLikedPost}
                    onClick={() => {
                      if (!dashboardTopLikedPost) return;
                      setSelectedPostId(dashboardTopLikedPost.id);
                      setGalleryIndex(0);
                      setDetailOpen(true);
                    }}
                  >
                    <div className="igv2-dashboard-summary-label">
                      <Heart size={16} />
                      <span>{t("最近 15 件の最高いいね", "Top likes in latest 15")}</span>
                    </div>
                    <div className="igv2-dashboard-summary-value">
                      <strong>{mediaSummaryTitle(dashboardTopLikedPost, t)}</strong>
                      <span>{dashboardTopLikedPost ? formatNumber(dashboardTopLikedPost.like_count) : "0"}</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    className="igv2-dashboard-summary-row igv2-dashboard-summary-row--button"
                    disabled={!dashboardTopCommentedPost}
                    onClick={() => {
                      if (!dashboardTopCommentedPost) return;
                      setSelectedPostId(dashboardTopCommentedPost.id);
                      setGalleryIndex(0);
                      setDetailOpen(true);
                    }}
                  >
                    <div className="igv2-dashboard-summary-label">
                      <MessageCircle size={16} />
                      <span>{t("最近 15 件の最高コメント", "Top comments in latest 15")}</span>
                    </div>
                    <div className="igv2-dashboard-summary-value">
                      <strong>{mediaSummaryTitle(dashboardTopCommentedPost, t)}</strong>
                      <span>{dashboardTopCommentedPost ? formatNumber(dashboardTopCommentedPost.comments_count) : "0"}</span>
                    </div>
                  </button>
                  <article className="igv2-dashboard-summary-row">
                    <div className="igv2-dashboard-summary-label">
                      <Circle size={14} fill="currentColor" />
                      <span>{t("接続状態", "Connection status")}</span>
                    </div>
                    <div className="igv2-dashboard-summary-value">
                      <strong>{dashboard.data?.account?.username ? `@${dashboard.data.account.username}` : connection.data?.connection?.account_name || t("未接続", "Not connected")}</strong>
                      <span>{t("接続中", "Connected")}</span>
                    </div>
                  </article>
                </div>
              </section>
              <div className="igv2-dashboard-minimal-grid">
                <section className="igv2-panel igv2-account-summary-panel">
                  <div className="igv2-panel-head">
                    <div>
                      <h3>{t("下書き", "Drafts")}</h3>
                      <p>{t("公開前の投稿だけをここで管理します", "Only pre-publish posts are managed here")}</p>
                    </div>
                  </div>
                  <div className="igv2-action-list">
                    {(draftPosts.slice(0, 3)).map((post) => (
                      <button
                        key={post.id}
                        type="button"
                        className="igv2-action-item"
                        onClick={() => {
                          setSelectedPostId(post.id);
                          setGalleryIndex(0);
                          setDetailOpen(true);
                        }}
                      >
                        <img src={post.thumb || FALLBACK_IMAGE} alt={post.title} className="igv2-action-thumb" />
                        <strong>{post.title}</strong>
                        <span className="igv2-inline-status">{t("下書き", "Draft")}</span>
                      </button>
                    ))}
                    {draftPosts.length === 0 ? (
                      <div className="igv2-empty-inline">{t("下書きはまだありません。", "No drafts yet.")}</div>
                    ) : null}
                  </div>
                </section>
              </div>
            </div>
          )}

          {tab === "inbox" && (
            <div className="igv2-inbox-layout">
              <section className="igv2-panel igv2-inbox-sidebar">
                <div className="igv2-panel-head">
                  <div>
                    <h3>{t("受信トレイ", "Inbox")}</h3>
                    <p>{formatNumber(pendingInboxCount)} {t("件の未対応会話", "pending conversations")}</p>
                  </div>
                </div>
                <div className="igv2-search igv2-search--full">
                  <Search size={14} />
                  <input value="" readOnly placeholder={t("ユーザー名やメッセージを検索", "Search users or messages")} />
                </div>
                <div className="igv2-list" ref={dmListRef}>
                  {conversations.loading || conversations.refreshingLatest ? (
                    <div className="igv2-fetch-banner igv2-fetch-banner--top">
                      <span className="igv2-fetch-pulse" aria-hidden="true" />
                      <span>{t("新しい会話を確認中...", "Refreshing conversations...")}</span>
                    </div>
                  ) : null}
                  {visibleConversations.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`igv2-list-item ${item.id === activeConversation?.id ? "active" : ""}`}
                      onClick={() => {
                        void handleConversationSelect(item);
                      }}
                    >
                      {item.avatar ? (
                        <img src={item.avatar} alt={item.name} className="igv2-avatar" />
                      ) : (
                        renderInitialAvatar(item.name)
                      )}
                      <div className="igv2-list-main">
                        <div className="igv2-list-title-row">
                          <div className="igv2-list-title-main">
                            {!item.isRead ? <span className="igv2-list-state-dot" aria-hidden="true" /> : null}
                            {item.isRead ? <span>{item.name}</span> : <strong>{item.name}</strong>}
                          </div>
                          <span>{item.time}</span>
                        </div>
                        <div className="igv2-list-handle">{item.handle}</div>
                        <p>{item.preview}</p>
                      </div>
                      <div className="igv2-list-side">
                        {item.unread > 0 ? <em>{item.unread}</em> : null}
                      </div>
                    </button>
                  ))}
                  {conversations.loadingMore ? (
                    <div className="igv2-list-loading">
                      <span className="igv2-fetch-pulse" aria-hidden="true" />
                      {t("さらに読み込み中...", "Loading more...")}
                    </div>
                  ) : null}
                </div>
              </section>

              <section className="igv2-panel igv2-thread igv2-thread-workspace">
                <div className="igv2-thread-head">
                  <div className="igv2-thread-user">
                    {activeConversation?.avatar ? (
                      <img
                        src={activeConversation.avatar}
                        alt={activeConversation.name}
                        className="igv2-avatar"
                      />
                    ) : (
                      renderInitialAvatar(activeConversation?.name || activeConversation?.handle || "")
                    )}
                    <div>
                      <strong>{activeConversation?.name || t("会話を選択してください", "Select a conversation")}</strong>
                      <span>{activeConversation?.handle || "@"}</span>
                    </div>
                  </div>
                </div>
                <div className="igv2-bubbles" ref={dmBubblesRef}>
                  {conversationDetail.loading || conversationDetail.refreshingLatest ? (
                    <div className="igv2-fetch-banner igv2-fetch-banner--overlay">
                      <span className="igv2-fetch-pulse" aria-hidden="true" />
                      <span>{t("最新メッセージを取得中...", "Refreshing messages...")}</span>
                    </div>
                  ) : null}
                  {conversationDetail.loadingOlder ? (
                    <div className="igv2-fetch-banner igv2-fetch-banner--top igv2-fetch-banner--subtle">
                      <span className="igv2-fetch-pulse" aria-hidden="true" />
                      <span>{t("以前のメッセージを読み込み中...", "Loading earlier messages...")}</span>
                    </div>
                  ) : null}
                  {messageGroups.length > 0 ? messageGroups.map((group) => (
                    <div key={group.heading} className="igv2-message-group">
                      <div className="igv2-date-heading">{group.heading}</div>
                      {group.items.map((message) => (
                        <div key={message.id} className={`igv2-message-row ${message.direction === "outbound" ? "igv2-message-row--self" : ""}`}>
                          {message.direction !== "outbound" ? (
                            activeConversation?.avatar ? (
                              <img src={activeConversation.avatar} alt={activeConversation.name} className="igv2-avatar igv2-avatar--small" />
                            ) : (
                              renderInitialAvatar(activeConversation?.name || activeConversation?.handle || "", "small")
                            )
                          ) : null}
                          <div className={`igv2-message-stack ${message.direction === "outbound" ? "igv2-message-stack--self" : ""}`}>
                            <div className={`igv2-bubble ${message.direction === "outbound" ? "igv2-bubble--self" : ""}`}>
                              {(() => {
                                const primaryAttachment = pickPrimaryAttachment(message.attachments ?? []);
                                const hasImageAttachment = isRenderableImageAttachment(primaryAttachment);
                                return (
                                  <>
                                    {hasImageAttachment ? (
                                      <a
                                        className="igv2-message-media"
                                        href={primaryAttachment?.url || "#"}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        <img src={primaryAttachment?.url || ""} alt={primaryAttachment?.name || t("受信画像", "Incoming image")} />
                                      </a>
                                    ) : null}
                                    {message.text?.trim() ? <p>{message.text}</p> : null}
                                    {!message.text?.trim() && !hasImageAttachment && primaryAttachment ? (
                                      <div className="igv2-message-attachment">
                                        <ImageIcon size={14} />
                                        <span>{primaryAttachment.name || t("添付ファイル", "Attachment")}</span>
                                      </div>
                                    ) : null}
                                    {!message.text?.trim() && !primaryAttachment ? (
                                      <p>{t(
                                        "このメッセージは表示できません（動画・共有コンテンツなどの可能性があります）",
                                        "This message can't be displayed (it may be a video or shared content).",
                                      )}</p>
                                    ) : null}
                                  </>
                                );
                              })()}
                            </div>
                            <div className={`igv2-bubble-meta ${message.direction === "outbound" ? "igv2-bubble-meta--self" : ""}`}>
                              <span className="igv2-bubble-time">{formatTimeOnly(message.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )) : (
                    <div className="igv2-bubble">{t("メッセージはまだありません。", "No messages yet.")}</div>
                  )}
                </div>
                <div className="igv2-composer">
                  <input
                    ref={dmAttachmentInputRef}
                    type="file"
                    accept="image/*"
                    className="igv2-sr-only"
                    onChange={(event) => chooseDmAttachment(event.target.files?.[0] ?? null)}
                  />
                  {dmAttachment ? (
                    <div className="igv2-dm-attachment-preview">
                      <img src={dmAttachment.previewUrl} alt="" />
                      <div>
                        <strong>{dmAttachment.file.name}</strong>
                        <span>{t("送信時にアップロードします", "Uploads when sent")}</span>
                      </div>
                      <button
                        type="button"
                        className="igv2-compose-icon-button"
                        aria-label={t("画像を外す", "Remove image")}
                        title={t("画像を外す", "Remove image")}
                        onClick={clearDmAttachment}
                        disabled={dmReplySending}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : null}
                  <div className="igv2-composer-row igv2-composer-row--rich">
                    <button
                      type="button"
                      className="igv2-compose-icon-button"
                      aria-label={t("添付", "Attach")}
                      title={t("画像を添付", "Attach image")}
                      onClick={() => dmAttachmentInputRef.current?.click()}
                      disabled={!activeConversation || dmReplySending}
                    >
                      <ImageIcon size={16} />
                    </button>
                    <input
                      value={dmReplyText}
                      maxLength={2000}
                      placeholder={t("メッセージを入力...", "Type a message...")}
                      disabled={!activeConversation || dmReplySending}
                      onChange={(event) => {
                        setDmReplyText(event.target.value);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && !event.shiftKey) {
                          event.preventDefault();
                          void sendDmReply();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="igv2-send-button"
                      onClick={() => void sendDmReply()}
                      disabled={!activeConversation || (!dmReplyText.trim() && !dmAttachment) || dmReplySending}
                    >
                      {dmReplySending ? <Loader2 size={14} className="igv2-spin" /> : <Send size={14} />}
                      {dmReplySending ? t("送信中...", "Sending...") : t("送信", "Send")}
                    </button>
                  </div>
                  <div className="igv2-composer-meta">
                    <span>{dmReplyText.length} / 2000</span>
                  </div>
                </div>
              </section>
            </div>
          )}

          {tab === "comments" && (
            <div className="igv2-comments-layout">
              <section className="igv2-panel igv2-comments-overview">
                <div className="igv2-panel-head">
                  <h3>{t("コメント一覧", "Comments")}</h3>
                </div>
                {commentOverviewItems.length > 0 || commentOverviewLoading ? (
                  <div className="igv2-list" ref={commentsListRef}>
                    {commentOverviewLoading ? (
                      <div className="igv2-fetch-banner igv2-fetch-banner--top">
                        <span className="igv2-fetch-pulse" aria-hidden="true" />
                        <span>
                          {temporaryCommentPostId
                            ? t("この投稿のコメントを確認中...", "Refreshing this post's comments...")
                            : t("新しいコメントを確認中...", "Refreshing comments...")}
                        </span>
                      </div>
                    ) : null}
                    {commentOverviewItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`igv2-list-item ${item.id === activeComment?.id ? "active" : ""}`}
                      onClick={() => {
                        if (!temporaryCommentPostId) {
                          setTemporaryCommentPostId(null);
                        }
                        setActiveCommentId(item.id);
                      }}
                    >
                        {renderInitialAvatar(item.author)}
                        <div className="igv2-list-main">
                          <div className="igv2-row-between">
                            <strong>{item.author}</strong>
                            <span>{item.time}</span>
                          </div>
                          <div className="igv2-comment-post-title">
                            {t("title:", "title:")} {resolveCommentPostTitle(item)}
                          </div>
                          <p>{item.comment}</p>
                            {item.replies.length > 0 ? (
                              <div className="igv2-comment-thread-preview">
                                <span>{item.replies.length}{t("件の返信", " replies")}</span>
                                <span>
                                  {item.replies[0]?.author}: {parseLeadingReplyMention(
                                    item.replies[0]?.comment || "",
                                    item.replies[0]?.parentAuthor,
                                  ).body}
                                </span>
                              </div>
                            ) : null}
                        </div>
                        <span className={`igv2-status igv2-status--${item.status}`}>{statusLabel[item.status]}</span>
                      </button>
                    ))}
                    {commentOverviewLoadingMore ? (
                      <div className="igv2-list-loading">
                        <span className="igv2-fetch-pulse" aria-hidden="true" />
                        {t("さらに読み込み中...", "Loading more...")}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="igv2-empty-state">
                    <strong>{hasCommentCountMismatch ? t("コメント明細を取得できていません", "Comment details are unavailable") : t("コメントはまだありません", "No comments yet")}</strong>
                    <p>
                      {hasCommentCountMismatch
                        ? t("投稿側ではコメント件数が見えていますが、コメント詳細 API から本文一覧を取得できていません。権限不足または Graph API 側の取得失敗の可能性があります。", "The post shows comment counts, but the comment detail API did not return the message list.")
                        : t("現在の接続アカウントの直近投稿にコメントが取得できていません。", "No recent comments were fetched for the connected account.")}
                    </p>
                  </div>
                )}
              </section>

              <section className="igv2-panel igv2-comments-workspace">
                {(activeComment || activeCommentPost) ? (
                  <div className="igv2-comments-workspace-stack">
                    <div className="igv2-comment-preview-card">
                      <div className="igv2-comment-preview-shell">
                        <img
                          src={activeCommentPost?.thumb || activeComment?.mediaThumb || FALLBACK_IMAGE}
                          alt={activeCommentPost?.title || activeCommentDetail?.media_caption || activeComment?.mediaTitle || t("Instagram 投稿", "Instagram post")}
                          className="igv2-comment-media"
                        />
                        <div className="igv2-comment-preview-summary">
                          <div className="igv2-comment-creator">
                            {renderInitialAvatar(activeComment?.author || dashboard.data?.account?.username || "Instagram", "small")}
                            <div>
                              <strong>{connection.data?.connection?.account_name || dashboard.data?.account?.username || activeComment?.author || "Instagram"}</strong>
                              <span className="igv2-comment-meta-time">
                                {activeCommentPost?.date || formatFullDateTime(activeCommentDetail?.created_at || null)}
                              </span>
                            </div>
                          </div>
                          <div className="igv2-comment-preview-meta">
                            <span>{statusLabel[activeCommentPost?.status || "published"] || t("公開済み", "Published")}</span>
                            <span>{activeCommentPost?.title || activeComment?.mediaTitle || t("Instagram 投稿", "Instagram post")}</span>
                            <span>{activeCommentPost?.caption || activeCommentDetail?.media_caption || activeComment.mediaTitle || t("投稿文はまだ取得されていません。", "Post copy has not been fetched yet.")}</span>
                          </div>
                          <div className="igv2-comment-stats-bar">
                            <div><strong>{formatNumber(activeCommentPost?.likes ?? null)}</strong><span>{t("いいね", "Likes")}</span></div>
                            <div><strong>{formatNumber(activeCommentPost?.comments ?? null)}</strong><span>{t("コメント", "Comments")}</span></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="igv2-comment-thread-card igv2-comment-thread-card--workspace">
                      <div className="igv2-comment-thread">
                        <div className="igv2-comment-thread-header">
                          <strong>{t("コメント", "Comments")} ({activePostCommentTotal})</strong>
                          <span>{activeCommentPost?.date || formatFullDateTime(activeCommentDetail?.created_at || null)}</span>
                        </div>
                        <div className="igv2-comment-stream" ref={commentStreamRef}>
                          {commentWorkspace.loading || commentWorkspace.refreshingLatest ? (
                            <div className="igv2-fetch-banner igv2-fetch-banner--top">
                              <span className="igv2-fetch-pulse" aria-hidden="true" />
                              <span>{t("この投稿のコメントを更新中...", "Refreshing this post's comments...")}</span>
                            </div>
                          ) : null}
                          {activePostCommentThreads.length > 0 ? activePostCommentThreads.map((item) => (
                            <div key={item.id} className={`igv2-comment-stream-item ${item.id === activeComment?.id ? "active" : ""}`}>
                              <div className="igv2-inline-user">
                                {renderInitialAvatar(item.author, "small")}
                                <div>
                                  <strong>{item.author}</strong>
                                  <span className="igv2-comment-meta-time">{item.time}</span>
                                </div>
                              </div>
                              <p>{item.comment}</p>
                              <div className="igv2-comment-stream-meta">
                                <span className={`igv2-status igv2-status--${item.status}`}>{statusLabel[item.status]}</span>
                                <button
                                  type="button"
                                  className="igv2-comment-stream-link"
                                  onClick={() => {
                                    setActiveCommentId(item.id);
                                    startCommentReply(item.id, item.author);
                                  }}
                                >
                                  <Send size={13} />
                                  {t("返信", "Reply")}
                                </button>
                                <button type="button" className="igv2-comment-stream-more">•••</button>
                              </div>
                              {item.replies.length > 0 ? (
                                <div className="igv2-comment-replies">
                                  {item.replies.map((reply) => {
                                    const parsedReply = parseLeadingReplyMention(reply.comment, reply.parentAuthor);
                                    return (
                                      <div key={reply.id} className="igv2-comment-reply-item">
                                        <div className="igv2-inline-user">
                                          {renderInitialAvatar(reply.author, "small")}
                                          <div>
                                            <strong>{reply.author}</strong>
                                            <span className="igv2-comment-meta-time">{reply.time}</span>
                                          </div>
                                        </div>
                                        <p>
                                          {parsedReply.mention ? <span className="igv2-comment-at">@{parsedReply.mention} </span> : null}
                                          {parsedReply.body}
                                        </p>
                                        <div className="igv2-comment-stream-meta igv2-comment-stream-meta--reply">
                                          <button
                                            type="button"
                                            className="igv2-comment-stream-link"
                                            onClick={() => {
                                              setActiveCommentId(item.id);
                                              startCommentReply(item.id, reply.author);
                                            }}
                                          >
                                            <Send size={13} />
                                            {t("返信", "Reply")}
                                          </button>
                                          <button type="button" className="igv2-comment-stream-more">•••</button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : null}
                            </div>
                          )) : (
                            <div className="igv2-detail-comment-empty igv2-detail-comment-empty--workspace">
                              <div className="igv2-detail-comment-empty-icon"><MessageCircle size={18} /></div>
                              <div>
                                <strong>{t("コメントはまだありません。", "No comments yet.")}</strong>
                                <p>{t("この投稿の最初のコメントをここから追加できます。", "You can add the first comment here.")}</p>
                              </div>
                            </div>
                          )}
                          {commentWorkspace.loadingMore ? (
                            <div className="igv2-list-loading igv2-list-loading--inline">
                              <span className="igv2-fetch-pulse" aria-hidden="true" />
                              {t("さらにコメントを読み込み中...", "Loading more comments...")}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="igv2-comment-reply-dock">
                        <div className="igv2-comment-reply-meta">
                          <span>{commentDockModeLabel}</span>
                          <strong>
                            {commentReplyTarget
                              ? `@${commentReplyTarget.targetUserName}`
                              : (activeCommentPost?.title || activeComment?.mediaTitle || t("当前帖子", "Current post"))}
                          </strong>
                        </div>
                        <input
                          value={commentDockValue}
                          placeholder={commentDockPlaceholder}
                          onChange={(e) => {
                            if (commentReplyTarget) {
                              handleCommentReplyChange(e.target.value);
                              return;
                            }
                            setCommentComposeBody(e.target.value);
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (commentReplyTarget) {
                              void sendCommentReply();
                              return;
                            }
                            if (commentComposeMediaId) {
                              void sendPostComment(commentComposeMediaId);
                            }
                          }}
                          disabled={
                            commentReplyTarget
                              ? !commentReplyBody.trim() || commentReplySending
                              : !commentComposeMediaId || !commentComposeBody.trim() || commentComposeSending
                          }
                        >
                          <Send size={15} />
                          {commentDockSending ? t("送信中...", "Sending...") : t("送信", "Send")}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="igv2-empty-state igv2-empty-state--workspace">
                    <strong>{t("返信対象のコメントがありません", "No comment available to reply")}</strong>
                    <p>{t("左側に一覧が出ていない場合、現在の Instagram アカウントから取得できたコメントが 0 件です。", "If nothing appears on the left, zero comments were fetched for this account.")}</p>
                  </div>
                )}
              </section>
            </div>
          )}

          {tab === "content" && (
            <div className="igv2-content-layout">
              <section className="igv2-panel igv2-panel--content">
                <div className="igv2-panel-head">
                  <div>
                    <h3>{t("コンテンツ管理", "Content")}</h3>
                    <p>{t("公開済み投稿を一覧し、右側で下書きを管理します", "Browse published posts and manage drafts on the side")}</p>
                  </div>
                  <div className="igv2-panel-actions">
                    <button
                      type="button"
                      className="igv2-chip igv2-chip--primary"
                      onClick={openComposerForCreate}
                    >
                      <Pencil size={14} /> {t("投稿を作成", "Create post")}
                    </button>
                  </div>
                </div>
                <div ref={contentListRef} className="igv2-content-scroll">
                  <div className="igv2-content-grid">
                    {publishedPosts.map((post) => (
                      <button
                        key={post.id}
                        type="button"
                        className="igv2-highlight-card igv2-post-card igv2-post-card--feature"
                        onClick={() => {
                          setSelectedPostId(post.id);
                          setGalleryIndex(0);
                          setDetailOpen(true);
                        }}
                      >
                        <img src={post.thumb || FALLBACK_IMAGE} alt={post.title} />
                        <span className={`igv2-status igv2-status--${post.status}`}>{statusLabel[post.status]}</span>
                        <div className="igv2-highlight-meta igv2-post-body">
                          <strong>{post.title}</strong>
                          <span>{post.date}</span>
                          <div className="igv2-post-metrics">
                            <span><Heart size={14} /> {formatNumber(post.likes)}</span>
                            <span><MessageCircle size={14} /> {formatNumber(post.comments)}</span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                  <div ref={contentLoadMoreRef} className="igv2-content-load-sentinel" aria-hidden="true" />
                  {media.loadingMore ? (
                    <div className="igv2-content-loading">
                      <span className="igv2-fetch-pulse" aria-hidden="true" />
                      <span>{t("さらに投稿を読み込み中...", "Loading more posts...")}</span>
                    </div>
                  ) : null}
                </div>
              </section>

              <div className="igv2-content-side-stack">
                <section className="igv2-panel igv2-side-panel">
                  <div className="igv2-panel-head"><h3>{t("投稿サマリー", "Post summary")}</h3></div>
                  <div className="igv2-summary">
                    <div><strong>{formatNumber(contentSummary.total)}</strong><span>{t("公開済み", "Published")}</span></div>
                    <div><strong>{formatNumber(contentSummary.scheduled)}</strong><span>{t("予約済み", "Scheduled")}</span></div>
                    <div><strong>{formatNumber(contentSummary.draft)}</strong><span>{t("下書き", "Drafts")}</span></div>
                  </div>
                </section>

                <section className="igv2-panel igv2-side-panel igv2-drafts-panel">
                  <div className="igv2-panel-head">
                    <div>
                      <h3>{t("下書き", "Drafts")}</h3>
                      <p>{t("公開前の投稿だけをここで管理します", "Only unpublished posts appear here")}</p>
                    </div>
                  </div>
                  <div className="igv2-draft-list">
                    {draftPosts.length > 0 ? draftPosts.map((post) => (
                      <div
                        key={post.id}
                        className={`igv2-draft-item ${selectedPost?.id === post.id ? "active" : ""}`}
                      >
                        <button
                          type="button"
                          className="igv2-draft-item-main"
                          onClick={() => {
                            setSelectedPostId(post.id);
                            setGalleryIndex(0);
                            setDetailOpen(true);
                          }}
                        >
                          <img
                            src={post.thumb || FALLBACK_IMAGE}
                            alt={post.title}
                            className="igv2-draft-thumb"
                          />
                          <div className="igv2-draft-copy">
                            <div className="igv2-draft-title-row">
                              <strong>{post.title}</strong>
                              <span className={`igv2-status igv2-status--${post.status}`}>{statusLabel[post.status]}</span>
                            </div>
                            <p>{post.caption || t("キャプション未設定", "No caption yet")}</p>
                            <span>{post.date}</span>
                          </div>
                        </button>
                        <div className="igv2-draft-item-actions">
                          <button
                            type="button"
                            className="igv2-draft-publish"
                            disabled={Boolean(busyOverlay)}
                            onClick={() => {
                              void handlePublishDraft(post.id);
                            }}
                          >
                            {t("今すぐ公開", "Publish now")}
                          </button>
                        </div>
                      </div>
                    )) : (
                      <div className="igv2-empty-state igv2-empty-state--compact">
                        <strong>{t("下書きはありません", "No drafts yet")}</strong>
                        <p>{t("投稿を保存するとここに表示されます。", "Saved drafts will appear here.")}</p>
                      </div>
                    )}
                  </div>
                </section>
              </div>
            </div>
          )}

        </div>
      </section>

      {detailOpen && selectedPost && (
        <div className="igv2-modal-backdrop" role="presentation" onClick={() => setDetailOpen(false)}>
          <div className="igv2-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <div className="igv2-modal-body igv2-modal-body--immersive">
              <div className="igv2-modal-media-column">
                <div className="igv2-gallery igv2-gallery--immersive">
                  <div
                    className={[
                      "igv2-gallery-stage",
                      "igv2-gallery-stage--immersive",
                      selectedPostIsVideo ? "igv2-gallery-stage--video" : "",
                      detailInfoVisible ? "" : "igv2-gallery-stage--preview",
                    ].filter(Boolean).join(" ")}
                  >
                    {!detailInfoVisible ? (
                      <div
                        className="igv2-preview-backdrop"
                        style={{ backgroundImage: `url(${previewBackdropImage})` }}
                        aria-hidden="true"
                      />
                    ) : null}
                    {!detailInfoVisible ? (
                      <div className={`igv2-device-preview igv2-device-preview--${previewFrameKind}`}>
                        {selectedPostIsVideo ? (
                          <video
                            key={`${selectedPost.id}:${selectedPost.videoUrl}`}
                            src={selectedPost.videoUrl ?? undefined}
                            poster={selectedPost.thumb ?? galleryImage}
                            controls
                            playsInline
                            preload="metadata"
                            onLoadedMetadata={(event) => classifyPreviewFrame(event.currentTarget.videoWidth, event.currentTarget.videoHeight)}
                          />
                        ) : (
                          <img
                            src={galleryImage}
                            alt={selectedPost.title}
                            onLoad={(event) => classifyPreviewFrame(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight)}
                          />
                        )}
                      </div>
                    ) : selectedPostIsVideo ? (
                      <video
                        key={`${selectedPost.id}:${selectedPost.videoUrl}`}
                        src={selectedPost.videoUrl ?? undefined}
                        poster={selectedPost.thumb ?? galleryImage}
                        controls
                        playsInline
                        preload="metadata"
                      />
                    ) : (
                      <img src={galleryImage} alt={selectedPost.title} />
                    )}
                    <div className="igv2-modal-stage-toolbar">
                      {detailInfoVisible ? (
                        <div className="igv2-modal-head-badge">
                          <ImageIcon size={16} />
                          <span>{t("投稿詳細", "Post details")}</span>
                        </div>
                      ) : <div />}
                      <div className="igv2-panel-actions">
                        {selectedPost.source === "draft" ? (
                          <button
                            type="button"
                            className="igv2-modal-action"
                            disabled={Boolean(busyOverlay)}
                            onClick={() => {
                              void handlePublishDraft(selectedPost.id);
                            }}
                          >
                            <Send size={14} /> {t("今すぐ公開", "Publish now")}
                          </button>
                        ) : null}
                        {selectedPost.source === "media" ? (
                          <button
                            type="button"
                            className="igv2-modal-action"
                            disabled={Boolean(busyOverlay) || postRefreshing}
                            onClick={() => {
                              void handleRefreshSelectedPost();
                            }}
                          >
                            <RefreshCw size={14} className={postRefreshing ? "igv2-spin" : undefined} />
                            {postRefreshing ? t("更新中...", "Refreshing...") : t("更新", "Refresh")}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="igv2-modal-action"
                          disabled={Boolean(busyOverlay)}
                          onClick={() => setDetailInfoVisible((current) => !current)}
                        >
                          <Info size={14} /> {detailInfoVisible ? t("情報を隠す", "Hide info") : t("情報を表示", "Show info")}
                        </button>
                        <button
                          type="button"
                          className="igv2-modal-action igv2-modal-action--danger"
                          disabled={Boolean(busyOverlay)}
                          onClick={handleDeleteSelectedPost}
                        >
                          {t("削除", "Delete")}
                        </button>
                        <button type="button" className="igv2-icon-button igv2-modal-close" onClick={() => setDetailOpen(false)} aria-label={t("閉じる", "Close")}>
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                    <div
                      className={[
                        "igv2-modal-stage-copy",
                        detailInfoVisible ? "" : "igv2-modal-stage-copy--focused",
                      ].filter(Boolean).join(" ")}
                    >
                      <div className="igv2-modal-subtitle"><ImageIcon size={18} /> {t("投稿詳細", "Post details")}</div>
                      <h3>{selectedPost.title}</h3>
                      <p>{selectedPost.source === "media" ? t("公開済み投稿", "Published post") : t("下書き / 予約投稿", "Draft / scheduled post")} · {selectedPost.date}</p>
                    </div>
                    {detailInfoVisible ? (
                      <aside className="igv2-modal-overlay">
                        <div className="igv2-modal-summary-stack">
                          <div className="igv2-account-pill igv2-account-pill--detail">
                            <div className="igv2-avatar igv2-avatar--brand">{getInitial(connection.data?.connection?.account_name || dashboard.data?.account?.username)}</div>
                            <div className="igv2-account-pill-copy">
                              <div className="igv2-account-pill-meta">
                                <strong>{dashboard.data?.account?.username || connection.data?.connection?.account_name || "Instagram"}</strong>
                                <span className={`igv2-status igv2-status--${selectedPost.status}`}>{statusLabel[selectedPost.status]}</span>
                              </div>
                            </div>
                          </div>
                          <div className="igv2-meta-block igv2-meta-block--detail">
                            <p>{selectedPost.caption || t("キャプション未設定", "No caption yet")}</p>
                          </div>
                        </div>
                        {selectedPost.source === "draft" ? (
                          <>
                            <div className="igv2-kpi-grid igv2-kpi-grid--detail">
                              <div className="igv2-detail-stat-card">
                                <span className="igv2-detail-stat-icon igv2-detail-stat-icon--images"><ImageIcon size={20} /></span>
                                <div><strong>{formatNumber(selectedPost.images.length)}</strong><span>{t("画像数", "Assets")}</span></div>
                              </div>
                              <div className="igv2-detail-stat-card">
                                <span className="igv2-detail-stat-icon igv2-detail-stat-icon--comment"><Tag size={20} /></span>
                                <div><strong>{statusLabel[selectedPost.status]}</strong><span>{t("状態", "Status")}</span></div>
                              </div>
                            </div>
                            <div className="igv2-side-section igv2-side-section--detail">
                              <strong>{t("履歴", "History")}</strong>
                              <ul className="igv2-timeline">
                                <li><CalendarDays size={16} /> {t("更新日時", "Updated")}: {selectedPost.date}</li>
                                <li><Tag size={16} /> {t("種別", "Type")}: {t("下書き", "Draft")}</li>
                                <li><CheckCircle2 size={16} /> {t("ステータス", "Status")}: {statusLabel[selectedPost.status]}</li>
                              </ul>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="igv2-kpi-grid igv2-kpi-grid--detail">
                              <div className="igv2-detail-stat-card">
                                <span className="igv2-detail-stat-icon igv2-detail-stat-icon--heart"><Heart size={20} /></span>
                                <div><strong>{formatNumber(selectedPost.likes)}</strong><span>{t("いいね", "Likes")}</span></div>
                              </div>
                              <button
                                type="button"
                                className="igv2-detail-stat-card igv2-detail-stat-card--button"
                                onClick={() => openCommentsWorkspaceForPost(selectedPost)}
                              >
                                <span className="igv2-detail-stat-icon igv2-detail-stat-icon--comment"><MessageCircle size={20} /></span>
                                <div><strong>{formatNumber(selectedPost.comments)}</strong><span>{t("コメント", "Comments")}</span></div>
                              </button>
                              <div className="igv2-detail-stat-card">
                                <span className="igv2-detail-stat-icon igv2-detail-stat-icon--images"><ImageIcon size={20} /></span>
                                <div><strong>{formatNumber(selectedPost.images.length)}</strong><span>{t("画像数", "Assets")}</span></div>
                              </div>
                            </div>
                            <div className="igv2-side-section igv2-side-section--detail">
                              <div className="igv2-row-between">
                                <strong>{t("最近のコメント", "Recent comments")}</strong>
                                <button type="button" onClick={() => openCommentsWorkspaceForPost(selectedPost)}>{t("すべて見る", "View all")}</button>
                              </div>
                              {selectedPostComments.length > 0 ? selectedPostComments.map((item) => (
                                <div key={item.id} className="igv2-mini-comment igv2-mini-comment--detail">
                                  <div className="igv2-avatar igv2-avatar--brand igv2-avatar--small">{getInitial(item.author)}</div>
                                  <div>
                                    <strong>
                                      {item.author}
                                      {item.isReply && item.parentAuthor ? t(` · ${item.parentAuthor} への返信`, ` · Reply to ${item.parentAuthor}`) : ""}
                                    </strong>
                                    <p>{item.comment}</p>
                                    <span className="igv2-muted-text">{item.time}</span>
                                  </div>
                                </div>
                              )) : (
                                <div className="igv2-detail-comment-empty">
                                  <div className="igv2-detail-comment-empty-icon"><MessageCircle size={18} /></div>
                                  <div>
                                    <strong>{t("コメントはまだありません。", "No comments yet.")}</strong>
                                    <p>{t("最初のコメントをお待ちしています。", "Waiting for the first comment.")}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="igv2-side-section igv2-side-section--detail">
                              <strong>{t("履歴", "History")}</strong>
                              <ul className="igv2-timeline">
                                <li><CalendarDays size={16} /> {t("更新日時", "Updated")}: {selectedPost.date}</li>
                                <li><Tag size={16} /> {t("種別", "Type")}: {t("公開投稿", "Published post")}</li>
                                <li><CheckCircle2 size={16} /> {t("ステータス", "Status")}: {statusLabel[selectedPost.status]}</li>
                              </ul>
                            </div>
                          </>
                        )}
                      </aside>
                    ) : null}
                    {selectedPost.images.length > 1 && (
                      <>
                        <button
                          type="button"
                          className="igv2-gallery-arrow left"
                          onClick={() => setGalleryIndex((current) => (current - 1 + selectedPost.images.length) % selectedPost.images.length)}
                        >
                          <ChevronLeft size={18} />
                        </button>
                        <button
                          type="button"
                          className="igv2-gallery-arrow right"
                          onClick={() => setGalleryIndex((current) => (current + 1) % selectedPost.images.length)}
                        >
                          <ChevronRight size={18} />
                        </button>
                      </>
                    )}
                    <div className="igv2-gallery-strip-wrap">
                      <div className="igv2-gallery-strip">
                        {selectedPost.images.map((image, index) => (
                          <button
                            key={`${selectedPost.id}-${index}`}
                            type="button"
                            className={galleryIndex === index ? "active" : ""}
                            onClick={() => setGalleryIndex(index)}
                            style={{ backgroundImage: `url(${image})` }}
                          >
                            <img src={image} alt="" />
                          </button>
                        ))}
                      </div>
                      <div className="igv2-gallery-counter igv2-gallery-counter--strip">{galleryIndex + 1}/{selectedPost.images.length}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <InstagramV2ComposerModal
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        onSaved={handleComposerSaved}
        initialTitle={composerInitialTitle}
        initialCaption={composerInitialCaption}
      />
    </div>
  );
};
